/**
 * MODAL ENREGISTREMENT PAIEMENT
 * ============================
 * Modal pour enregistrer un paiement client sur une facture logistics
 *
 * Fonctionnalites:
 * - Saisie montant (validation contre balance_due)
 * - Selection mode de paiement
 * - Date et reference de paiement
 * - Calcul automatique nouveau solde
 * - Mise a jour automatique statut facture (via trigger DB)
 *
 * Workflow:
 * 1. Utilisateur ouvre le modal depuis une facture
 * 2. Saisit montant <= balance_due
 * 3. Selectionne mode paiement (optionnel)
 * 4. Clique "Enregistrer" -> Backend + Trigger MAJ auto
 * 5. Modal se ferme -> Callback onSuccess pour refresh
 *
 * @see src/gestion-logistics/hooks/useCustomerPayments.ts
 * @see routes/logistics/customer-payments.js
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Select,
  Alert,
  Space,
  Typography,
  Divider,
  Card,
  Row,
  Col,
  Statistic,
  message,
} from 'antd';
import {
  DollarOutlined,
  CalendarOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import apiClient from '../../api/apiClient';
import dayjs from 'dayjs';
import API_CONFIG from '../../config/api';
import { useSelection } from '../../SelectionContext';
import { useTreasuryAccountSuggestion } from '../../hooks/useCommercialTreasurySuggestion';
import useCustomerPayments from '../hooks/useCustomerPayments';
import { Invoice } from '../types/invoice.types';
import { TreasuryAccount } from '../types/treasury.types';

const { TextArea } = Input;
const { Text } = Typography;
const { Option } = Select;

interface PaymentRecordModalProps {
  visible: boolean;
  invoice: Invoice | null;
  onClose: () => void;
  onSuccess?: () => void;
}

const PaymentRecordModal: React.FC<PaymentRecordModalProps> = ({
  visible,
  invoice,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const { recordPayment, loading } = useCustomerPayments();
  const { selectedCompany, selectedWarehouse } = useSelection();

  const [paymentAmount, setPaymentAmount] = useState<number>(0);
  const [treasuryAccounts, setTreasuryAccounts] = useState<TreasuryAccount[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(false);

  const [paymentModes, setPaymentModes] = useState<Array<{ id: number; name: string }>>([]);
  const { onModeChange: suggestAccount, onAccountChange: selectAccount } = useTreasuryAccountSuggestion(form, {
    warehouseId: selectedWarehouse, companyId: selectedCompany, moduleScope: 'logistics',
    enabled: visible && !!invoice, accounts: treasuryAccounts,
    modeField: 'payment_mode_id', accountField: 'treasury_account_id',
  });

  const currencyLabel = invoice?.currency_symbol || 'XOF';

  // Calculer le nouveau solde apres paiement
  const newBalance = useMemo(() => {
    if (!invoice) return 0;
    const currentBalance = invoice.balance_due || 0;
    return Math.max(0, currentBalance - paymentAmount);
  }, [invoice, paymentAmount]);

  // Nouveau statut prevu apres paiement
  const newStatus = useMemo(() => {
    if (!invoice) return '';
    if (newBalance === 0) return 'paid';
    if (newBalance < (invoice.balance_due || 0)) return 'partial';
    return invoice.payment_status || 'unpaid';
  }, [invoice, newBalance]);

  // Fonction utilitaire pour formater les montants
  const formatAmount = (amount: number | null | undefined): string => {
    if (amount === null || amount === undefined || isNaN(amount)) return '0';
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Math.round(amount));
  };

  const formatCurrency = (amount: number | null | undefined): string => {
    const formatted = formatAmount(amount);
    return `${formatted} ${currencyLabel}`;
  };

  // Reinitialiser le formulaire a l'ouverture
  useEffect(() => {
    if (visible && invoice) {
      form.resetFields();
      setPaymentAmount(0);
      form.setFieldsValue({
        payment_date: dayjs(),
        amount: invoice.balance_due,
      });
      setPaymentAmount(invoice.balance_due || 0);
    }
  }, [visible, invoice, form]);

  useEffect(() => {
    if (!visible) return undefined;
    const controller = new AbortController();
    setTreasuryAccounts([]);
    setPaymentModes([]);
    form.setFieldsValue({ treasury_account_id: undefined, payment_mode_id: undefined });
    const fetchAccounts = async () => {
      if (!selectedCompany || !selectedWarehouse) {
        setAccountsLoading(false);
        return;
      }

      setAccountsLoading(true);
      try {
        const response = await apiClient.get(
          `${API_CONFIG.API_URL}/logistics/payments/options`,
          {
            signal: controller.signal,
            params: {
              company_id: selectedCompany,
              warehouse_id: selectedWarehouse,
            },
          }
        );

        if (!controller.signal.aborted && response.data?.success) {
          const accounts = (response.data.accounts || []).filter((account: TreasuryAccount) =>
            !invoice?.currency_id || Number(account.currency_id) === Number(invoice.currency_id));
          setTreasuryAccounts(accounts);
          setPaymentModes(response.data.modes || []);

        }
      } catch (error) {
        if (!controller.signal.aborted) message.error('Les options de paiement ne sont pas disponibles. Rouvrez le formulaire pour réessayer.');
      } finally {
        if (!controller.signal.aborted) setAccountsLoading(false);
      }
    };

    fetchAccounts();
    return () => controller.abort();
  }, [form, selectedCompany, selectedWarehouse, visible, invoice?.currency_id]);

  // Handler changement montant
  const handleAmountChange = (value: number | null) => {
    setPaymentAmount(value || 0);
  };

  // Soumettre le formulaire
  const handleSubmit = async () => {
    try {
      if (!invoice) {
        message.error('Facture client non specifiee');
        return;
      }

      const values = await form.validateFields();

      if (values.amount <= 0) {
        message.warning('Le montant doit etre positif');
        return;
      }

      if (values.amount > (invoice.balance_due || 0)) {
        message.warning('Le montant depasse le solde du');
        return;
      }

      let paymentModeName = undefined;
      if (values.payment_mode_id) {
        const selectedMode = paymentModes.find((mode) => mode.id === values.payment_mode_id);
        paymentModeName = selectedMode?.name;
      }

      const paymentData = {
        invoice_id: invoice.id,
        payment_date: values.payment_date ? values.payment_date.format('YYYY-MM-DD') : undefined,
        amount: values.amount,
        payment_reference: values.payment_reference || undefined,
        payment_mode_id: values.payment_mode_id || undefined,
        payment_mode_name: paymentModeName,
        treasury_account_id: values.treasury_account_id,
        currency_id: invoice.currency_id || undefined,
        notes: values.notes || undefined,
      };

      const result = await recordPayment(paymentData);

      if (result) {
        message.success('Paiement enregistre avec succes');
        onClose();
        if (onSuccess) onSuccess();
      }
    } catch (error) {
      console.error('[PaymentRecordModal] Erreur validation:', error);
      message.error('Erreur lors de l\'enregistrement du paiement');
    }
  };

  if (!invoice) return null;

  return (
    <Modal
      title={
        <Space>
          <DollarOutlined />
          <span>Enregistrer un Paiement</span>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={700}
      okText="Enregistrer le paiement"
      cancelText="Annuler"
      onOk={handleSubmit}
      confirmLoading={loading}
      okButtonProps={{ disabled: accountsLoading || treasuryAccounts.length === 0 }}
      destroyOnClose
    >
      {invoice && (
        <Alert
          message={`Facture ${invoice.invoice_number} - ${invoice.client_name}`}
          description={
            <div>
              <Text>
                Montant total : <strong>{formatCurrency(invoice.total_amount)}</strong>
              </Text>
              <br />
              <Text type="success">
                Deja paye : <strong>{formatCurrency(invoice.paid_amount)}</strong>
              </Text>
              <br />
              <Text type="danger">
                Solde du : <strong>{formatCurrency(invoice.balance_due)}</strong>
              </Text>
            </div>
          }
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Form form={form} layout="vertical">
        {treasuryAccounts.length === 0 && (
          <Alert
            message="Aucun compte de tresorerie"
            description="Creez un compte de tresorerie pour enregistrer ce paiement."
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Montant du paiement"
              name="amount"
              rules={[
                { required: true, message: 'Montant requis' },
                {
                  validator: (_, value) => {
                    if (!value || value <= 0) {
                      return Promise.reject('Le montant doit etre positif');
                    }
                    if (value > (invoice.balance_due || 0)) {
                      return Promise.reject(
                        `Ne peut pas depasser le solde du (${formatCurrency(invoice.balance_due)})`
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
              tooltip="Montant a payer (ne peut pas depasser le solde du)"
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                max={invoice.balance_due || 0}
                precision={0}
                formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                parser={(value) => Number(value!.replace(/\s/g, ''))}
                placeholder="Montant"
                prefix={<DollarOutlined />}
                suffix={currencyLabel}
                onChange={handleAmountChange}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label="Date du paiement"
              name="payment_date"
              rules={[{ required: true, message: 'Date requise' }]}
            >
              <DatePicker
                style={{ width: '100%' }}
                format="DD/MM/YYYY"
                placeholder="Selectionner la date"
                prefix={<CalendarOutlined />}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label="Compte de tresorerie"
          name="treasury_account_id"
          rules={[{ required: true, message: 'Compte requis' }]}
        >
          <Select
            placeholder="Selectionner un compte"
            loading={accountsLoading}
            disabled={treasuryAccounts.length === 0}
            onChange={selectAccount}
          >
            {treasuryAccounts.map((account) => (
              <Option key={account.id} value={account.id}>
                {account.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Reference de paiement" name="payment_reference">
              <Input
                placeholder="Ex: CHQ-12345, VIR-98765"
                prefix={<FileTextOutlined />}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item label="Mode de paiement" name="payment_mode_id">
              <Select placeholder="Selectionner un mode" allowClear loading={accountsLoading} onChange={suggestAccount}>
                {paymentModes.map((mode) => (
                  <Option key={mode.id} value={mode.id}>
                    {mode.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item label="Notes" name="notes">
          <TextArea
            rows={2}
            placeholder="Notes ou remarques pour ce paiement..."
            maxLength={300}
            showCount
          />
        </Form.Item>
      </Form>

      <Divider>Apercu du nouveau solde</Divider>

      <Row gutter={[16, 16]}>
        <Col span={8}>
          <Card size="small">
            <Statistic
              title="Solde actuel"
              value={invoice.balance_due || 0}
              formatter={(value) => formatCurrency(value as number)}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>

        <Col span={8}>
          <Card size="small">
            <Statistic
              title="Montant a payer"
              value={paymentAmount}
              formatter={(value) => formatCurrency(value as number)}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>

        <Col span={8}>
          <Card size="small">
            <Statistic
              title="Nouveau solde"
              value={newBalance}
              formatter={(value) => formatCurrency(value as number)}
              prefix={<CheckCircleOutlined />}
              valueStyle={{
                color: newBalance === 0 ? '#52c41a' : '#faad14',
                fontWeight: 'bold',
              }}
            />
            <div style={{ marginTop: 8, textAlign: 'center' }}>
              {newStatus === 'paid' && (
                <Text type="success" style={{ fontSize: '12px' }}>
                  Facture sera marquee comme PAYEE
                </Text>
              )}
              {newStatus === 'partial' && (
                <Text type="warning" style={{ fontSize: '12px' }}>
                  Paiement PARTIEL
                </Text>
              )}
            </div>
          </Card>
        </Col>
      </Row>
    </Modal>
  );
};

export default PaymentRecordModal;
